// JavaScript files are compiled and minified during the build process to the assets/built folder. See available scripts in the package.json file.

// Import CSS
import "../css/index.css";
import 'lazysizes';

// Core modules - always loaded
import { initTheme, handleThemeToggle } from './modules/theme.js';
import { initActiveNavigation, initSecondaryDropdown, initMobileSidebar, initSidebarCollapse } from './modules/navigation.js';
import { initSearch, initSearchBarButton } from './modules/search.js';
import { initAnnouncementProcessor } from './modules/announcement-processor.js';

// Page-specific modules - conditionally loaded
import { initTOC, initTOCAccordion } from './modules/toc.js';
import { initPostImagesLightbox } from './modules/lightbox.js';
import { initTitleCaseProcessor } from './modules/title-case.js';
import { initCardSymbolProcessor } from './modules/symbols.js';
import { initCardsListFilter } from './modules/cards.js';
import { initSimpleInfiniteScroll } from './modules/infinite-scroll.js';
import { initFootnotes, initTooltipSingleton } from './modules/footnotes.js';
import { initColorProcessor } from './modules/color-processor.js';
import { initSizeProcessor } from './modules/size-processor.js';
import { initCenterHeading } from './modules/center-heading.js';
import { processQuoteAuthors } from './modules/quote-author.js';
import { processAllSnippets } from './modules/snippet-processor.js';
import { initContentCleanup } from './modules/content-cleanup.js';
import { initImageSpacing } from './modules/image-spacing.js';
import { initResponsiveImages } from './modules/responsive-images.js';
import { initShareCopy } from './modules/share.js';
import { initManageCookies } from './modules/manage-cookies.js';
import { initCookiesConsent } from './modules/cookies-consent.js';
import { initAnalytics } from './modules/analytics.js';
import { initLinkTracker } from './modules/tracking/link-tracker.js';
import { initScrollDepth } from './modules/tracking/scroll-depth.js';

// Initialize theme immediately (before DOM ready to prevent flash)
initTheme();
handleThemeToggle();

// Initialize core features when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Core navigation features - always needed
  initActiveNavigation();
  initSecondaryDropdown();
  initMobileSidebar();
  initSidebarCollapse();

  // Search - always needed (search button in header)
  initSearch();
  initSearchBarButton();

  initManageCookies();
  initCookiesConsent();
  initAnalytics();
  initLinkTracker();

  // Announcement bar processor - process color snippets
  initAnnouncementProcessor();

  // Cards list - only if cards-list sections exist
  initCardsListFilter();

  // Infinite scroll for tag/author pages - only if specific section exists
  initSimpleInfiniteScroll();

  // Post/page specific features
  const contentElement = document.querySelector('.gh-content');
  const isPostPage = document.body.classList.contains('post-template') ||
                     document.body.classList.contains('page-template') ||
                     contentElement;

  if (isPostPage && contentElement) {
    // Responsive images — rewrite inline content images to use ?w= (must run before lightbox)
    initResponsiveImages();

    // TOC - only on post/page templates
    initTOC();
    initTOCAccordion();

    // Lightbox for images - only on post/page templates
    setTimeout(() => {
      initPostImagesLightbox();
    }, 100);

    // Title case processor - only on post/page templates
    initTitleCaseProcessor();

    // Card symbol processor - only on post/page templates
    initCardSymbolProcessor();

    // 1. Collect tooltip definitions from the post content
    const { footnotesMap } = initFootnotes();

    // 2. Initialize the single shared tooltip popover (must come before processAllSnippets
    //    so that refs created inside nested snippets get bound to the same popover)
    initTooltipSingleton(footnotesMap);

    // 3. Process all structural snippets recursively (toggle, width, reveal-answer,
    //    interactive-reveal-answer, icon blocks, grid) — tooltips inside nested
    //    containers are handled as part of the recursion
    processAllSnippets(contentElement, footnotesMap);

    // 4. Flat post-processing passes (run after structure is fully built)
    initColorProcessor();
    initSizeProcessor();
    initCenterHeading();
    processQuoteAuthors(contentElement);

    // 5. Image spacing + content cleanup (must run last)
    initImageSpacing();
    initContentCleanup();

    // Share button - copy current page link
    initShareCopy();

    // Scroll-depth analytics — last so the content has its final height
    initScrollDepth();

    // Reveal content after all snippets are processed
    setTimeout(() => {
      contentElement.classList.add('snippets-ready');
    }, 100);
  }
});
